import {
  observable,
  set as mobxSet,
  computed,
  action,
  makeObservable,
} from 'mobx';
import moment from 'moment';

import { post } from 'jsx/utils/axiosWrapper';
import { Initializable } from 'jsx/mixins';
import BaseStore from 'jsx/stores/baseStore';
import { trackEligibilityFailure } from 'jsx/utils/metrics';
import URLS from 'jsx/auth/constants/urls';
import { INELIGIBLE_REASONS } from 'jsx/auth/constants/eligibility';
import useStores from 'jsx/utils/hooks/useStores';
import { isDefined } from 'jsx/utils/isDefined';
import CONFIG from 'jsx/constants/config';

import {
  REASON_KEY,
  ZIP_KEY,
  DOB_KEY,
  STATE_KEY,
  VIEW_ORDER,
  ACCEPTED_TOS_KEY,
} from './constants';
import {
  setCookieWithNullHandling,
  getFromCookieOrStorage,
  removeFromCookieAndStorage,
} from './helpers';
import type { ErrorResponse } from './types';

class EligibilityStore extends Initializable(BaseStore) {
  @observable
  ineligibleReason: string | null = null;

  @observable
  dateOfBirth: string | null = null;

  @observable
  acceptedTOS = false;

  @observable
  state: string | null = null;

  constructor() {
    super();
    makeObservable(this);
  }

  @action
  onInitialize = () => {
    const ineligibleReason = getFromCookieOrStorage(REASON_KEY);
    const dateOfBirth = getFromCookieOrStorage(DOB_KEY);
    const state = getFromCookieOrStorage(STATE_KEY);
    const tosCookie = getFromCookieOrStorage(ACCEPTED_TOS_KEY);
    const acceptedTOS = Boolean(Number(tosCookie)) || tosCookie === 'true';

    this.hydrate({
      ineligibleReason,
      dateOfBirth,
      state,
      acceptedTOS,
    });
    return Promise.resolve();
  };

  @action
  hydrate = (data: {
    ineligibleReason?: string | null;
    dateOfBirth?: string | null;
    state?: string | null;
    acceptedTOS?: boolean;
  }) => {
    mobxSet(this, data);
  };

  @action
  setGuestState = (state: string | null) => {
    setCookieWithNullHandling(STATE_KEY, state);
    this.state = state;
  };

  @action
  setIneligibleReason = (ineligibleReason: string | null) => {
    setCookieWithNullHandling(REASON_KEY, ineligibleReason);

    this.hydrate({ ineligibleReason });
  };

  @action
  setAcceptedTos = (acceptedTOS: boolean) => {
    setCookieWithNullHandling(ACCEPTED_TOS_KEY, String(Number(acceptedTOS)));
    this.acceptedTOS = acceptedTOS;
  };

  @action
  setDateOfBirth = (dateOfBirth: string | null) => {
    setCookieWithNullHandling(DOB_KEY, dateOfBirth);
    this.hydrate({ dateOfBirth });
  };

  @computed
  get canRegister() {
    return !!this.canViewLocation(URLS.register);
  }

  @computed
  get isFromEligibleState() {
    return isDefined(this.state);
  }

  @computed
  get hasEligibleDOB() {
    return (
      !!this.dateOfBirth &&
      this.ineligibleReason !== INELIGIBLE_REASONS.ineligibleAge
    );
  }

  @computed
  get isIneligible() {
    return !!this.ineligibleReason;
  }

  @computed
  get farthestView() {
    if (!this.isFromEligibleState || !this.hasEligibleDOB) {
      return URLS.welcome;
    }
    return URLS.register;
  }

  @computed
  get registrationData() {
    return {
      dob: this.dateOfBirth,
      acceptedTOS: this.acceptedTOS,
      state: this.state,
    };
  }

  clearStoredAttributes = () => {
    removeFromCookieAndStorage(REASON_KEY);
    removeFromCookieAndStorage(ZIP_KEY);
    removeFromCookieAndStorage(DOB_KEY);
    removeFromCookieAndStorage(STATE_KEY);
    removeFromCookieAndStorage(ACCEPTED_TOS_KEY);
  };

  @action
  getDateOfBirthEligibility = (data: { dob: string; state?: string }) => {
    this.setDateOfBirth(data.dob);

    return post(URLS.api.dobEligibility(), data).then((response) => {
      this.setIneligibleReason(null);
      return response;
    }, this.onDateOfBirthFailure);
  };

  saveEmail = (email: string) => {
    const dob = this.dateOfBirth
      ? moment(this.dateOfBirth, 'MM/DD/YYYY').format('YYYY-MM-DD')
      : null;
    const data = {
      email,
      dob,
      reason: this.ineligibleReason,
      state: this.state,
    };

    return post(URLS.api.emailEligibility(), data);
  };

  @action
  onDateOfBirthFailure = (error: ErrorResponse) => {
    const errorMessages = error.response?.data.errors;

    if (!errorMessages) {
      return Promise.reject();
    }

    if (isDefined(errorMessages.isIneligibleAge)) {
      this.setIneligibleReason(INELIGIBLE_REASONS.ineligibleAge);
      trackEligibilityFailure({
        reason: CONFIG.isAgencyApp
          ? 'Agency user under 18'
          : 'Curology user under 13',
        dob: this.dateOfBirth,
      });
    }

    return Promise.reject(this.ineligibleReason);
  };

  private canViewLocation = (location: string) => {
    const farthestViewableLocation = this.farthestView;

    const locationIndex = VIEW_ORDER.indexOf(location);
    const farthestViewableIndex = VIEW_ORDER.indexOf(farthestViewableLocation);

    if (farthestViewableIndex < 0) {
      return true;
    }

    return farthestViewableIndex >= locationIndex;
  };
}

export const useEligibilityStore = () => {
  const { eligibilityStore } = useStores<EligibilityStore>();

  return { eligibilityStore };
};

export default EligibilityStore;
